import { categoryHeading, readableCategoryName } from '../api/accounts';
import type { DdPhaseSummary, DdRequest } from '../api/types';
import {
  itemFulfillment,
  matchesChecklistFilter,
  parseChecklistFilter,
  type ChecklistFilter,
} from './checklist-status';

/**
 * The checklist as it is shown (checklist redesign slice 3), for the admin and
 * the seller alike: the whole checklist the caller can read, narrowed by the
 * filter tile, the phase picker and the search box, then grouped per phase into
 * FLAT category groups — one group per category path, headed by the path as a
 * breadcrumb — in list order. Pure; every count is exact because the whole
 * checklist is loaded once.
 */

/** A change to the checklist's view: only the named keys change (null/empty
 * clears the phase or the search). */
export interface ChecklistViewChange {
  filter?: ChecklistFilter;
  phaseId?: string | null;
  query?: string;
}

/** The checklist view a page address holds: the item filter (`?filter=`, or the
 * page's default), the phase (`?phase=`, null: every phase) and the search (`?q=`). */
export function readChecklistView(
  params: URLSearchParams,
  defaultFilter: ChecklistFilter,
): { filter: ChecklistFilter; phaseId: string | null; query: string } {
  return {
    filter: parseChecklistFilter(params.get('filter')) ?? defaultFilter,
    phaseId: params.get('phase') || null,
    query: params.get('q') ?? '',
  };
}

/** The page address after a view change — any of filter, phase and search —
 * for ONE setSearchParams call. setSearchParams' updater gets the render-time
 * params, so two calls in one handler would each start from the same params and
 * the last would win. A key set to null/empty is dropped; a key not named is
 * kept; every other parameter is kept. */
export function withChecklistView(prev: URLSearchParams, next: ChecklistViewChange): URLSearchParams {
  const p = new URLSearchParams(prev);
  const write = (key: string, value: string | null | undefined) => {
    if (value === undefined) return;
    if (value) p.set(key, value);
    else p.delete(key);
  };
  write('filter', next.filter);
  write('phase', next.phaseId);
  write('q', next.query);
  return p;
}

/** One category of one phase: the items shown under it, and its true counts. */
export interface CategoryGroupView {
  /** A DOM id for the heading (the outline jumps to it). Stable per phase + path. */
  id: string;
  /** The category path, root → leaf (never empty — ungrouped items are separate). */
  path: string[];
  /** "Bank Docs › Account Statements › Bank accounts — Chase ••4821". */
  heading: string;
  /** The outline's label: the last two levels of the path, as people read them
   * ("Tax Docs › Federal (IRS)") — the leaf alone is ambiguous across parents. */
  leaf: string;
  /** The items shown under the current filter, phase and search. */
  items: DdRequest[];
  /** Every item of the category, whatever is shown. */
  all: DdRequest[];
  total: number;
  missing: number;
}

export interface PhaseView {
  phase: DdPhaseSummary;
  /** The phase id, or 'legacy' for the phaseless bucket. */
  key: string;
  /** Shown items with no category (directly under the phase). */
  uncategorized: DdRequest[];
  groups: CategoryGroupView[];
  /** How many of the phase's items are shown. */
  shown: number;
}

/** The words of a search, lower-cased; an item matches when every word appears in
 * its title, detail or category path. */
export function searchTokens(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean);
}

export function matchesSearch(req: DdRequest, tokens: readonly string[]): boolean {
  if (tokens.length === 0) return true;
  const haystack = [
    req.title,
    req.detail ?? '',
    ...req.categoryPath.map((seg) => readableCategoryName(seg)),
    ...req.categoryPath,
  ]
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

function hash(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** The key a category group is remembered by (collapsed state, ids). */
export function groupKey(phaseKey: string, path: readonly string[]): string {
  return `${phaseKey}:${path.join('\u0000')}`;
}

export function categoryGroupId(phaseKey: string, path: readonly string[]): string {
  return `cat-${hash(groupKey(phaseKey, path))}`;
}

/** The breadcrumb for a category path: every segment readable, the leaf with its
 * accounts or states when it is a placeholder. */
export function breadcrumb(path: readonly string[], items: readonly DdRequest[]): string {
  return path
    .map((seg, i) => (i === path.length - 1 ? categoryHeading(seg, items) : readableCategoryName(seg)))
    .join(' › ');
}

export function buildPhaseViews({
  rollup,
  items,
  filter,
  phaseId,
  query,
}: {
  /** The per-phase rollup (phases in order, the legacy bucket last). */
  rollup: readonly DdPhaseSummary[];
  /** The whole room's items, in list order. */
  items: readonly DdRequest[];
  filter: ChecklistFilter;
  /** Only this phase — its id, or 'legacy' for the phaseless bucket (null: every phase). */
  phaseId: string | null;
  query: string;
}): PhaseView[] {
  const tokens = searchTokens(query);
  const narrowing = filter !== 'all' || tokens.length > 0;
  const byPhase = new Map<string | null, DdRequest[]>();
  for (const r of items) {
    const list = byPhase.get(r.phaseId) ?? [];
    list.push(r);
    byPhase.set(r.phaseId, list);
  }
  const views: PhaseView[] = [];
  for (const phase of rollup) {
    const key = phase.phaseId ?? 'legacy';
    if (phaseId !== null && key !== phaseId) continue;
    const phaseItems = byPhase.get(phase.phaseId) ?? [];
    const shownOf = (r: DdRequest) =>
      matchesChecklistFilter(itemFulfillment(r), filter) && matchesSearch(r, tokens);
    const uncategorized: DdRequest[] = [];
    const groups = new Map<string, CategoryGroupView>();
    let shown = 0;
    for (const r of phaseItems) {
      const visible = shownOf(r);
      if (visible) shown += 1;
      if (r.categoryPath.length === 0) {
        if (visible) uncategorized.push(r);
        continue;
      }
      const k = groupKey(key, r.categoryPath);
      let group = groups.get(k);
      if (!group) {
        group = {
          id: categoryGroupId(key, r.categoryPath),
          path: [...r.categoryPath],
          heading: '',
          leaf: '',
          items: [],
          all: [],
          total: 0,
          missing: 0,
        };
        groups.set(k, group);
      }
      group.all.push(r);
      group.total += 1;
      if (itemFulfillment(r) === 'missing') group.missing += 1;
      if (visible) group.items.push(r);
    }
    const shownGroups = [...groups.values()].filter((g) => g.items.length > 0 || !narrowing);
    for (const g of shownGroups) {
      g.heading = breadcrumb(g.path, g.all);
      g.leaf = breadcrumb(g.path.slice(-2), g.all);
    }
    // A phase with nothing shown is left out while narrowing; under "All" with no
    // search every phase shows, empty ones included.
    if (narrowing && shown === 0) continue;
    views.push({ phase, key, uncategorized, groups: shownGroups, shown });
  }
  return views;
}
